import {
	Box,
	Flex,
	Text,
	Select as ChakraSelect,
	Select,
} from "@chakra-ui/react";
import {
	type Command,
	CommandStatus,
	CommandType,
} from "../../../types/command.types";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, {
	type Cell,
} from "../../../components/table/ServerSideTable";
import { useCommandsPaginated } from "../../../hooks/commands.hooks";
import { addBusinessDays, format } from "date-fns";
import { useAdmins, useConnectedUser } from "../../../hooks/user.hooks";
import i18n from "../../../i18n";
import { updateCommand } from "../../../mutations/command.mutations";
import { useMutation, useQueryClient } from "react-query";
import PrimaryButton from "../../../components/PrimaryButton";
import CartIcon from "../../../components/icons/CartIcon";
import { NewOrderEarSide, useNewOrderStore } from "../../../stores/order.store";
import {
	useCenterCommandsPaginated,
	useCenters,
} from "../../../hooks/centers.hooks";
import Tooltip from "../../../components/Tooltip";
import EarBadge from "../../../components/EarBadge";
import CommandTypeBadge from "../../../components/CommandTypeBadge";
import OrderStatusBadge from "../../../components/OrderStatusBadge";
import CommandStatusBadge from "../../../components/CommandStatusBadge";
import OrderSelectCenter from "../filters/OrdersSelectCenter";
import { useOrdersKanbanStore } from "../../../stores/kanban.store";
import OrdersSearch from "../filters/OrdersSearch";
import OrdersResetFilters from "../filters/OrdersResetFilters";
import OrdersSubToolbar from "../components/OrdersSubToolbar";
import OrdersDraftStatusFilter from "../filters/OrdersDraftStatusFilter";
import { useAdminSettings } from "../../../hooks/admin_settings.hooks";
import { useSimpleToast } from "../../../hooks/toast.hooks";
import type { PaginatedFilter } from "../../../types/axios.types";

function OrdersTableCenterCell({
	centerName,
	centerId,
}: {
	centerName?: string;
	centerId?: number;
}) {
	const user = useConnectedUser();
	const { setFilterCenterId } = useOrdersKanbanStore();

	if (!centerName) {
		return (
			<Text fontSize="14px" color="gray.500">
				---
			</Text>
		);
	}
	return (
		<Text
			fontSize="14px"
			textTransform="capitalize"
			textColor="brand.700"
			overflow="hidden"
			textOverflow="ellipsis"
			whiteSpace="nowrap"
			onClick={(evt) => {
				if (user?.isSuperAdmin) {
					evt.stopPropagation();
					evt.preventDefault();
					setFilterCenterId(centerId ? centerId.toString() : undefined);
				}
			}}
		>
			{centerName}
		</Text>
	);
}

function OrdersTablePatientCell({
	firstName,
	lastName,
}: {
	firstName?: string;
	lastName?: string;
}) {
	return (
		<Text
			fontSize="14px"
			textTransform="capitalize"
			textColor="brand.700"
			overflow="hidden"
			textOverflow="ellipsis"
			whiteSpace="nowrap"
		>
			{firstName}&nbsp;{lastName}
		</Text>
	);
}

function OrdersTableMaterialTypeCell({ order }: { order: Command }) {
	const variants = order?.draft?.variants;
	const variant = Array.isArray(variants)
		? variants.find((variant) =>
				variant.label.toLocaleLowerCase().includes("matière"),
			)
		: undefined;
	const form = order?.draft?.hasRightEar
		? order?.draft?.rightEarForm
		: order?.draft?.leftEarForm;
	if (variant === undefined || form === undefined || !(variant.id in form)) {
		return <Text fontSize={"12px"}>---</Text>;
	}
	return <Text fontSize={"12px"}>{form[variant.id]?.label ?? "---"}</Text>;
}

const SelectAdminBadge = ({
	value,
	onChange,
}: {
	value?: number;
	onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}) => {
	const { data: admins } = useAdmins();

	return (
		<ChakraSelect
			bgColor={"color.100"}
			color="color.700"
			fontSize="14px"
			h="21px"
			borderRadius="xl"
			value={value ?? undefined}
			onChange={(evt) => onChange && onChange(evt)}
		>
			<option value="">---</option>
			{admins &&
				admins.map((admin) => (
					<option key={admin.id} value={admin.id ?? undefined}>
						{admin.firstName}
					</option>
				))}
		</ChakraSelect>
	);
};

const OrdersTableToolbar = () => {
	const {
		search,
		filters: { centerId, status },
		setFilterCenterId,
		setSearch,
		setFilterStatus,
		setFilterNotStatus,
	} = useOrdersKanbanStore();
	const qc = useQueryClient();

	return (
		<Flex justifyContent="space-between">
			<Flex gap={2}>
				<OrderSelectCenter id={centerId} setId={setFilterCenterId} />
				<OrdersSearch search={search} setSearch={setSearch} />
				<OrdersDraftStatusFilter
					status={status as CommandStatus}
					setStatus={setFilterStatus}
					setNotStatus={setFilterNotStatus}
				/>
			</Flex>
			<Flex justifyContent="space-between">
				<Flex gap={4} alignItems="center">
					<OrdersResetFilters />
					<Select
						w="200px"
						placeholder="Statut"
						value={status}
						onChange={(evt) => {
							setFilterStatus(evt.target.value as CommandStatus);
							qc.invalidateQueries("commands");
						}}
					>
						{Object.values(CommandStatus)
							.filter((status) => status !== CommandStatus.PENDING)
							.map((status) => (
								<option key={status} value={status}>
									{i18n.t(`command.status.${status}`)}
								</option>
							))}
					</Select>
				</Flex>
			</Flex>
		</Flex>
	);
};

function OrdersTableView() {
	const user = useConnectedUser();
	const { center } = useCenters();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { filters: kanbanFilters, search } = useOrdersKanbanStore();
	const { data: adminSettings } = useAdminSettings();
	const toast = useSimpleToast();
	const { mutate: mutateUpdateCommand } = useMutation(updateCommand, {
		onSuccess: async () => {
			await queryClient.invalidateQueries();
		},
	});
	const { reset, load } = useNewOrderStore();
	const commandsQuery = useCommandsPaginated({});
	const centerCommandsQuery = useCenterCommandsPaginated({ id: center?.id });
	const paginatedQuery = user?.isSuperAdmin
		? commandsQuery
		: centerCommandsQuery;
	const externalFilters = useMemo<PaginatedFilter>(() => {
		return Object.entries({
			centerId: kanbanFilters.centerId,
			modelerId: kanbanFilters.modelerId,
			status: kanbanFilters.status,
			notStatus: kanbanFilters.notStatus,
			material: kanbanFilters.material,
			type: kanbanFilters.type,
			halted:
				kanbanFilters.halted === undefined
					? undefined
					: kanbanFilters.halted
						? "true"
						: "false",
		}).reduce((acc, [key, value]) => {
			if (value === undefined || value === "") {
				return acc;
			}
			acc[key] = value;
			return acc;
		}, {} as PaginatedFilter);
	}, [kanbanFilters]);

	const OrdersTableNoData = () => {
		return (
			<Flex justify="center">
				<Flex w="100%" justifyContent="center">
					<Flex
						w="100%"
						rounded="xl"
						bg="white"
						textAlign="center"
						lineHeight="44px"
						color="gray.500"
						direction={"column"}
						justifyContent={"center"}
						alignItems={"center"}
						py="100px"
					>
						<Flex
							w="48px"
							h="48px"
							bg="zinc.50"
							border="solid 1px"
							borderColor="brand.100"
							justifyContent="center"
							alignItems="center"
							borderRadius={100}
							mr="8px"
						>
							<CartIcon w="24px" h="24px" color="brand.700"></CartIcon>
						</Flex>
						<Text
							mt={4}
							color={"gray.900"}
							fontSize={"20px"}
							lineHeight={"30px"}
							fontWeight={600}
						>
							Aucune commande
						</Text>
						<PrimaryButton
							w="30rem"
							mt={4}
							onClick={() => {
								if (adminSettings?.disableCommandCreation) {
									toast.error(
										"Commande",
										"La création de commandes est désactivée.",
									);
									return;
								}
								reset();
								navigate("/orders/new");
							}}
						>
							Créer une commande
						</PrimaryButton>
					</Flex>
				</Flex>
			</Flex>
		);
	};
	const columns = useMemo<ColumnDef<Command>[]>(() => {
		const innerColumns: ColumnDef<Command>[] = [
			{
				accessorFn: (row) => row.exposedId,
				cell: (info) => {
					const id = info.row.original.exposedId
						.split("-")
						.slice(0, 3)
						.join("-");
					return (
						<Tooltip content={`${id}`}>
							<Box
								textOverflow={"ellipsis"}
								whiteSpace={"nowrap"}
								overflow={"hidden"}
								fontSize={"14px"}
							>
								{id}
							</Box>
						</Tooltip>
					);
				},
				id: "id",
				header: "ID",
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.center.name ?? "---",
				cell: (info) => (
					<OrdersTableCenterCell
						centerName={
							info.row.original.center ? info.row.original.center.name : "---"
						}
						centerId={info.row.original.centerId}
					></OrdersTableCenterCell>
				),
				id: "center",
				header: "Centre",
				enableSorting: false,
			},
			{
				accessorFn: (row) => row.patientId,
				cell: (info) => (
					<OrdersTablePatientCell
						firstName={info.row.original.patient?.firstName}
						lastName={info.row.original.patient?.lastName}
					></OrdersTablePatientCell>
				),
				id: "patient",
				header: "Patient",
				enableSorting: false,
			},
			{
				accessorFn: (row) => row.status,
				id: "status",
				header: "Statut",
				cell: (info) => {
					if (user?.isSuperAdmin) {
						return (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									margin: "0px 10px 0px 0px",
								}}
							>
								<CommandStatusBadge id={info.row.original.id} editable={true} />
							</div>
						);
					}
					return (
						<div style={{ padding: "0px 12px 0px 0px" }}>
							<OrderStatusBadge
								halted={info.row.original.haltedAt != null}
								status={info.row.original.status}
								width={"100%"}
							></OrderStatusBadge>
						</div>
					);
				},
				enableSorting: false,
			},
			{
				accessorFn: (row) => row.type,
				id: "type",
				header: "Type",
				cell: (info) => (
					<CommandTypeBadge type={info.row.original.type}></CommandTypeBadge>
				),
				enableSorting: false,
			},
			{
				accessorFn: (row) => row.type,
				cell: (info) => {
					if (info.row.original.type === CommandType.SUPPLY) {
						return null;
					}
					const draft = info.row.original.draft;
					if (draft === undefined) {
						return null;
					}
					const hasRight = draft?.hasRightEar;
					const hasLeft = draft?.hasLeftEar;
					return (
						<Flex gap={1}>
							{hasRight && <EarBadge side={NewOrderEarSide.RIGHT}></EarBadge>}
							{hasLeft && <EarBadge side={NewOrderEarSide.LEFT}></EarBadge>}
						</Flex>
					);
				},
				id: "ear",
				header: "Oreilles",
				enableSorting: false,
			},
			{
				accessorFn: (row) => row.id,
				cell: (info) => (
					<OrdersTableMaterialTypeCell order={info.row.original} />
				),
				id: "material",
				header: "Matériau",
				enableSorting: false,
			},
			{
				accessorFn: (row) =>
					row.groupExpectedShippingDate ?? row.expectedShippingDate,
				cell: (info) => {
					const expectedShippingDate =
						info.row.original.groupExpectedShippingDate ??
						info.row.original.expectedShippingDate;
					if (!expectedShippingDate) return null;

					const baseDate = new Date(expectedShippingDate);
					if (Number.isNaN(baseDate.getTime())) return null;

					const deliveryDate =
						adminSettings?.meanDaysShippingTime != null
							? addBusinessDays(baseDate, adminSettings.meanDaysShippingTime)
							: baseDate;

					return (
						<Text fontSize={"12px"}>{format(deliveryDate, "dd/MM/yyyy")}</Text>
					);
				},
				id: "deliveredAt",
				header: "Livraison",
				sortDescFirst: true,
			},
		];
		if (user?.isSuperAdmin) {
			innerColumns.push({
				accessorFn: (row) => row.modelerId,
				cell: (info) => (
					<SelectAdminBadge
						value={info.row.original.modelerId}
						onChange={(event) => {
							mutateUpdateCommand({
								id: info.row.original.id,
								modelerId:
									event.target.value !== ""
										? Number.parseInt(event.target.value)
										: undefined,
							});
						}}
					></SelectAdminBadge>
				),
				id: "modelerId",
				header: "Assigné à",
				enableSorting: false,
			});
		}
		return innerColumns;
	}, [adminSettings?.meanDaysShippingTime, mutateUpdateCommand, user]);

	if (user?.isSuperAdmin === undefined || center === null) return null;
	return (
		<DataTable
			columns={columns}
			defaultSortBy={[
				{
					id: "id",
					desc: true,
				},
			]}
			externalSearch={search}
			externalFilters={externalFilters}
			paginatedQuery={paginatedQuery}
			w={[
				"140px",
				"160px",
				"160px",
				"200px",
				"100px",
				"100px",
				"100px",
				"100px",
				"10rem",
			]}
			flexGrow={[0, 1, 1, 0, 0, 0, 1, 0]}
			cellLink={(cell: Cell) => {
				if (cell.row.status === CommandStatus.DRAFT) {
					return undefined;
				}
				if (cell.id.endsWith("managerId") || cell.id.endsWith("status")) {
					return undefined;
				}
				return `/orders/${cell.row.id}`;
			}}
			handleCellClick={(cell: Cell) => {
				if (cell.row.status === CommandStatus.DRAFT) {
					load({
						...cell.row.draft,
						command: { ...cell.row, draft: undefined },
					});
					navigate("/orders/new");
				}
			}}
			noDataComponent={OrdersTableNoData}
			toolbar={
				<Flex my={4} gap={4} direction="column">
					<OrdersTableToolbar />
					<OrdersSubToolbar />
				</Flex>
			}
		></DataTable>
	);
}

export default OrdersTableView;
